import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const AutoHedgeContext = createContext();

export const AutoHedgeProvider = ({ children }) => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Stable cache for demo data
  const demoDataCache = useRef({});

  const getExchangeConfig = (exchangeId) => {
    const configs = {
      okx: {
        name: 'OKX Futures Demo',
        demoBalance: { USDT: 100000, BTC: 10 },
        features: ['125x Leverage', 'Perpetual Futures', 'Real-time Data', 'Advanced Orders']
      },
      kraken: {
        name: 'Kraken Futures Demo', 
        demoBalance: { USD: 50000, BTC: 5 },
        features: ['50x Leverage', 'Regulated Exchange', 'Professional Tools', 'Risk Management']
      },
      binance: {
        name: 'Binance Futures Demo',
        demoBalance: { USDT: 100000, BTC: 10 },
        features: ['125x Leverage', 'Largest Volume', 'Advanced Charts', 'API Trading']
      }
    };
    return configs[exchangeId] || configs.okx;
  };

  const connectAccount = useCallback(async (exchangeId) => {
    setIsLoading(true);
    try {
      console.log(`🔗 CONTEXT: Connecting to ${exchangeId} demo environment...`);
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const exchangeConfig = getExchangeConfig(exchangeId);
      const accountData = {
        id: `${exchangeId}_${Date.now()}`,
        name: exchangeConfig.name,
        exchange: exchangeId,
        connected_at: new Date().toISOString(),
        status: 'connected',
        demo: true,
        balance: exchangeConfig.demoBalance,
        features: exchangeConfig.features,
        api_status: 'demo_mode'
      };

      console.log(`✅ CONTEXT: ${exchangeConfig.name} connected successfully (Demo Mode)`);
      
      setConnectedAccounts(prev => {
        const newAccounts = [...prev, accountData];
        console.log(`🛡️ CONTEXT: Updated connected accounts:`, newAccounts.length);
        return newAccounts;
      });
      
      return { success: true, account: accountData };
    } catch (error) {
      console.error(`❌ CONTEXT: ${exchangeId} connection failed:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const disconnectAccount = useCallback((accountId) => {
    console.log(`🔌 CONTEXT: Disconnecting account: ${accountId}`);
    setConnectedAccounts(prev => {
      const newAccounts = prev.filter(acc => acc.id !== accountId);
      console.log(`🛡️ CONTEXT: Updated accounts after disconnect:`, newAccounts.length);
      return newAccounts;
    });
    
    // Clear cache for disconnected account
    if (demoDataCache.current[accountId]) {
      delete demoDataCache.current[accountId];
    }
  }, []);

  const resetAccounts = useCallback(() => {
    console.log(`🔄 CONTEXT: Resetting all connected accounts`);
    setConnectedAccounts([]);
    demoDataCache.current = {};
  }, []);

  const simulateExchangeAPI = useCallback((exchangeId, method, params = {}) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        switch(method) {
          case 'fetchPositions':
            // Return CACHED stable data
            if (!demoDataCache.current[exchangeId]) {
              demoDataCache.current[exchangeId] = [{
                id: `pos_${exchangeId}_stable`,
                symbol: 'BTC/USDT',
                size: 0.5,
                entry_price: 105500,
                mark_price: 106000,
                unrealized_pnl: 250,
                margin: 5000,
                leverage: 10,
                side: 'long',
                exchange: exchangeId,
                last_updated: Date.now(),
                demo: true
              }];
              console.log(`📊 CONTEXT: Generated STABLE demo positions for ${exchangeId}`);
            } else {
              console.log(`📊 CONTEXT: Using CACHED demo positions for ${exchangeId}`);
            }
            resolve(demoDataCache.current[exchangeId]);
            break;
          case 'fetchBalance':
            resolve(getExchangeConfig(exchangeId).demoBalance);
            break;
          default:
            resolve({ success: true });
        }
      }, 200);
    });
  }, []);

  const contextValue = {
    connectedAccounts,
    isLoading,
    connectAccount,
    disconnectAccount,
    resetAccounts,
    simulateExchangeAPI
  };

  return (
    <AutoHedgeContext.Provider value={contextValue}>
      {children}
    </AutoHedgeContext.Provider>
  );
};

export const useAutoHedgeContext = () => {
  const context = useContext(AutoHedgeContext);
  if (!context) {
    throw new Error('useAutoHedgeContext must be used within AutoHedgeProvider');
  }
  return context;
};
